<template>
  <div class="zipCityWrapper">
    <dx-description-label v-if="$te(`${translationKeyPrefix}.description`)">
      {{ $t(`${translationKeyPrefix}.description`) }}
    </dx-description-label>

    <dx-input
      :id="config.id"
      v-model="zip"
      :label="translatedZipLabel"
      :disabled="config.readonly"
      :error="translatedZipError"
      type="text"
      class="item"
      @blur="onBlur('zip')"
      @input="onZipInput"
      @complete="onZipComplete"
      @enter="onZipComplete"
    />

    <dx-select
      v-if="citiesForZipCode.length >= 1"
      :value="city"
      :options="cityOptions"
      :label="translatedCityLabel"
      :error="translatedCityError"
      :disabled="citiesForZipCode.length === 1"
      class="item"
      @input="updateCity"
      @blur="onBlur('city')"
    />
    <dx-input
      v-else
      :value="city"
      :label="translatedCityLabel"
      :error="translatedCityError"
      :disabled="true"
      class="item"
      autocomplete="off"
    />
  </div>
</template>

<script>
import { DxInput, DxSelect } from '@sumcumo/dextra-frontend-component-lib'

import CitiesByZipQuery from '~/components/DX-ZipToCityInput/__gql__/queries/citiesByZip.gql'
import DxDescriptionLabel from '~/components/DX-DescriptionLabel/DxDescriptionLabel'

export default {
  name: 'DXZipCityAdapter',
  components: {
    DxDescriptionLabel,
    DxInput,
    DxSelect,
  },
  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
    type: {
      type: String,
      default: 'text',
    },
    value: {
      type: Object,
      default: () => ({ city: '', zip: null }),
    },
    error: {
      type: Object,
      default: () => ({ city: null, zip: null }),
    },
  },
  data() {
    return {
      zip: this.value.zip,
      city: this.value.city,
      citiesForZipCode: [],
      isZipComplete: true,
      fetchingCities: 0,
    }
  },
  computed: {
    translationKeyPrefix() {
      let prefix = ''

      if (this.config.source) {
        prefix = `${this.config.source.key}.${this.config.source.originalId}`

        if (this.config.source.kind === 'values') {
          prefix = `attributes.${prefix}`
        }
      }

      return prefix
    },

    translatedZipLabel() {
      return this.$t(`${this.translationKeyPrefix}.label.zip`)
    },

    translatedCityLabel() {
      return this.$t(`${this.translationKeyPrefix}.label.city`)
    },

    cityOptions() {
      const defaultOption = {
        value: '',
        label: this.$t('selectOption'),
        caption: true,
      }
      return [
        defaultOption,
        ...this.citiesForZipCode.map((city) => ({
          value: city.name,
          label: city.name,
        })),
      ]
    },

    translatedZipError() {
      if (this.isZipComplete && this.zip) {
        if (this.isLiechtenstein()) {
          return this.$t('errors.zipCity.zip.liechtenstein', {
            label: this.translatedZipLabel,
          })
        }

        if (!this.fetchingCities && this.citiesForZipCode.length === 0) {
          return this.$t('errors.zipCity.zip.invalid', {
            label: this.translatedZipLabel,
          })
        }
      }

      if (!this.error?.zip) {
        return null
      }

      return this.$t(`errors.zipCity.zip.${this.error.zip.type}`, {
        ...this.error.params,
        label: this.translatedZipLabel,
      })
    },

    translatedCityError() {
      if (!this.error?.city) {
        return null
      }

      return this.$t(`errors.zipCity.city.${this.error.city.type}`, {
        ...this.error.city.params,
        label: this.translatedCityLabel,
      })
    },
  },
  watch: {
    value: {
      handler(newValue) {
        this.zip = newValue.zip
        this.city = newValue.city
        this.isZipComplete = true
        this.fetchCities()
      },
    },
  },
  async created() {
    await this.fetchCities()
  },
  methods: {
    // eslint-disable-next-line complexity, max-statements
    async fetchCities() {
      if (
        !this.zip ||
        this.zip.length < 4 ||
        Number.isNaN(this.zip) ||
        this.isLiechtenstein()
      ) {
        this.citiesForZipCode = []
        this.updateCity('')
        return
      }

      this.fetchingCities += 1
      let cities
      try {
        const { data } = await this.$apollo.query({
          query: CitiesByZipQuery,
          variables: {
            zips: [this.zip],
            locale: this.$i18n.locale.toUpperCase(),
          },
        })
        // eslint-disable-next-line prefer-destructuring
        cities = data.cities
      } catch (e) {
        console.error(e)
      }
      this.fetchingCities -= 1

      if (cities !== undefined) {
        this.citiesForZipCode = [...cities]
        if (cities.length === 1) {
          this.updateCity(cities[0].name)
        }
      }
    },

    updateCity(city) {
      if (this.city !== city) {
        this.city = city
        this.emitValue()
      }
    },

    // eslint-disable-next-line complexity
    emitValue() {
      if (!this.isZipComplete || this.fetchingCities) {
        // We are in the middle of an input, do not emit
        return
      }

      if (
        this.isLiechtenstein() ||
        (this.zip && this.citiesForZipCode.length === 0)
      ) {
        // The input is invalid, do not emit
        return
      }

      // The input is valid, emit
      this.$emit('input', {
        zip: this.zip,
        city: this.city,
      })
    },

    onBlur(propKey) {
      this.$emit('validate', `${this.config.id}.${propKey}`)
    },

    async onZipInput() {
      this.isZipComplete = false
      await this.fetchCities()
    },

    onZipComplete() {
      this.isZipComplete = true
      this.emitValue()
    },

    isLiechtenstein() {
      const liechtensteinZipCodes = this.$t('liechtensteinZipCodes')
        .split(',')
        .map((zipCode) => zipCode.trim())
      return liechtensteinZipCodes.includes(this.zip)
    },
  },
}
</script>
<style lang="scss" scoped>
.item {
  margin-bottom: rem(32);

  &:last-of-type {
    margin-bottom: 0;
  }
}
</style>
